/**
 * VueLayers
 * Web map Vue components with the power of OpenLayers
 *
 * @package vuelayers
 * @author Vladimir Vershinin <ghettovoice@gmail.com>
 * @version 0.11.37
 * @license MIT
 * @copyright (c) 2017-2021, Vladimir Vershinin <ghettovoice@gmail.com>
 */
import _mapInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/map';
import { EPSG_3857, EPSG_4326 } from './consts';
import Feature from 'ol/Feature';
import { get, isPlainObject } from '../util/minilo';
import { createGeoJsonFmt } from './format';

var geoJsonFmt = createGeoJsonFmt();
/**
 * @param {Feature} feature
 * @param {ProjectionLike|undefined} [featureProjection=EPSG:3857]
 * @param {ProjectionLike|undefined} [dataProjection=EPSG:4326]
 * @return {Object}
 */

function writeGeoJsonFeature(feature) {
  var featureProjection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EPSG_3857;
  var dataProjection = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : EPSG_4326;
  var geoJsonFeature = geoJsonFmt.writeFeatureObject(feature, {
    featureProjection: featureProjection,
    dataProjection: dataProjection
  });

  if (Array.isArray(get(geoJsonFeature, 'properties.features'))) {
    var _context;

    geoJsonFeature.properties.features = _mapInstanceProperty(_context = geoJsonFeature.properties.features).call(_context, function (feature) {
      if (feature instanceof Feature) {
        return writeGeoJsonFeature(feature, featureProjection, dataProjection);
      }

      return feature;
    });
  }

  return geoJsonFeature;
}
/**
 * @param {Object} geoJsonFeature
 * @param {ProjectionLike|undefined} [featureProjection=EPSG:3857]
 * @param {ProjectionLike|undefined} [dataProjection=EPSG:4326]
 * @return {Feature}
 */

function readGeoJsonFeature(geoJsonFeature) {
  var featureProjection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EPSG_3857;
  var dataProjection = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : EPSG_4326;
  var feature = geoJsonFmt.readFeature(geoJsonFeature, {
    featureProjection: featureProjection,
    dataProjection: dataProjection
  });

  if (Array.isArray(feature.get('features'))) {
    var _context2;

    feature.set('features', _mapInstanceProperty(_context2 = feature.get('features')).call(_context2, function (feature) {
      if (isPlainObject(feature)) {
        return readGeoJsonFeature(feature, featureProjection, dataProjection);
      }

      return feature;
    }));
  }

  return feature;
}
/**
 * @param {Geometry} geometry
 * @param {ProjectionLike|undefined} [geometryProjection=EPSG:3857]
 * @param {ProjectionLike|undefined} [dataProjection=EPSG:4326]
 * @return {Object}
 */

function writeGeoJsonGeometry(geometry) {
  var geometryProjection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EPSG_3857;
  var dataProjection = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : EPSG_4326;
  return geoJsonFmt.writeGeometryObject(geometry, {
    featureProjection: geometryProjection,
    dataProjection: dataProjection
  });
}
/**
 * @param {Object|Object} geoJsonGeometry
 * @param {ProjectionLike|undefined} [geometryProjection=EPSG:3857]
 * @param {ProjectionLike|undefined} [dataProjection=EPSG:4326]
 * @return {Geometry}
 */

function readGeoJsonGeometry(geoJsonGeometry) {
  var geometryProjection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EPSG_3857;
  var dataProjection = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : EPSG_4326;
  dataProjection = readProjection(geoJsonGeometry, dataProjection);
  return geoJsonFmt.readGeometry(geoJsonGeometry, {
    featureProjection: geometryProjection,
    dataProjection: dataProjection
  });
}
function readProjection(geoJsonObj, defaultProjection) {
  return geoJsonFmt.readProjection(geoJsonObj) || defaultProjection;
}

export { writeGeoJsonFeature, readGeoJsonFeature, writeGeoJsonGeometry, readGeoJsonGeometry, readProjection };
